// Shared, dependency-free CRM contract used by the client and server.
export const stages = {
  prospect: "À contacter", contacted: "Premier contact", qualified: "Besoin identifié",
  demo: "Démonstration", pilot: "Essai pilote", proposal: "Soumission",
  booked: "Réservée", completed: "Activité réalisée", lost: "Sans suite",
} as const;
export const quoteStatuses = { draft: "Brouillon", sent: "Transmise", accepted: "Acceptée", declined: "Refusée" } as const;
export const activityTypes = { call: "Appel", email: "Courriel", meeting: "Rencontre", demo: "Démonstration", pilot: "Essai pilote", followup: "Relance", feedback: "Bilan", note: "Note" } as const;
export type Stage = keyof typeof stages;
export type QuoteStatus = keyof typeof quoteStatuses;
export interface CompanyInput {
  companyName: string; contactName: string; contactRole: string; email: string; phone: string;
  city: string; sector: string; source: string; service: "team_building" | "event" | "promotion" | "both";
  stage: Stage; priority: "normal" | "high"; owner: string; objective: string; needs: string;
  participants: number | null; budget: number | null; eventDate: string; location: string;
  nextFollowUp: string; nextAction: string; demoDate: string; pilotDate: string;
  contactBasis: "unknown" | "response_only" | "express" | "published" | "withdrawn";
  consentEvidence: string; consentDate: string; outcome: string; feedback: string;
}
export interface Company extends CompanyInput { id: string; revision: number; inquiryId?: string }
export interface QuoteLine { description: string; quantity: number; unitPriceCents: number }
export interface QuoteInput {
  title: string; validUntil: string; eventDate: string; participants: number | null;
  location: string; duration: string; scope: string; requirements: string;
  conditions: string; lines: QuoteLine[]; discountCents: number;
  gstRate: number; qstRate: number; depositPercent: number;
}
export interface Totals { subtotalCents: number; discountCents: number; taxableCents: number; gstCents: number; qstCents: number; totalCents: number; depositCents: number }
export interface Quote extends QuoteInput {
  id: string; companyId: string; number: string; revision: number; status: QuoteStatus;
  company: Pick<CompanyInput, "companyName" | "contactName" | "email" | "phone">;
  totals: Totals; decisionNote?: string; statusDate?: string;
}
export function todayToronto(date = new Date()) {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "America/Toronto", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date).map(v => [v.type, v.value]));
  return `${p.year}-${p.month}-${p.day}`;
}
export function money(cents: number) { return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(cents / 100) }
export function dateLabel(date: string) { return date ? new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium", timeZone: "UTC" }).format(new Date(date + "T12:00:00Z")) : "À préciser" }
export function text(value: unknown, label: string, max: number, required = false) {
  if (typeof value !== "string") throw new Error(`${label} invalide.`);
  const v = value.trim();
  if (v.length > max || (required && !v)) throw new Error(`${label} : ${required ? "1" : "0"} à ${max} caractères.`);
  return v;
}
export function dateOnly(value: unknown, label: string) {
  const v = text(value, label, 10);
  if (v && (!/^\d{4}-\d{2}-\d{2}$/.test(v) || !Number.isFinite(Date.parse(v + "T12:00:00Z")) || new Date(v + "T12:00:00Z").toISOString().slice(0, 10) !== v)) throw new Error(`${label} invalide.`);
  return v;
}
function choice<T extends string>(value: unknown, choices: readonly T[], label: string): T {
  if (typeof value !== "string" || !choices.includes(value as T)) throw new Error(`${label} invalide.`);
  return value as T;
}
function numeric(value: unknown, label: string, max: number, integer = false): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > max || (integer && !Number.isInteger(value))) throw new Error(`${label} invalide.`);
  return value;
}
export function cleanCompany(d: Record<string, unknown>): CompanyInput {
  const out = {} as CompanyInput;
  const fields = { companyName: 120, contactName: 100, contactRole: 100, email: 180, phone: 30, city: 100, sector: 100, source: 180, owner: 100, objective: 500, needs: 3000, location: 300, nextAction: 300, consentEvidence: 1000, outcome: 1000, feedback: 3000 };
  for (const [key, max] of Object.entries(fields)) (out as unknown as Record<string, unknown>)[key] = text(d[key], key, max, key === "companyName");
  out.email = out.email.toLowerCase();
  if (out.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(out.email)) throw new Error("Courriel invalide.");
  for (const key of ["eventDate", "nextFollowUp", "demoDate", "pilotDate", "consentDate"] as const) out[key] = dateOnly(d[key], key);
  out.stage = choice(d.stage, Object.keys(stages) as Stage[], "Étape");
  out.priority = choice(d.priority, ["normal", "high"], "Priorité");
  out.service = choice(d.service, ["team_building", "event", "promotion", "both"], "Service");
  out.contactBasis = choice(d.contactBasis, ["unknown", "response_only", "express", "published", "withdrawn"], "Autorisation de contact");
  out.participants = d.participants === null ? null : numeric(d.participants, "Participants", 10000, true);
  out.budget = d.budget === null ? null : numeric(d.budget, "Budget", 1000000);
  return out;
}
export function calculateTotals(d: Pick<QuoteInput, "lines" | "discountCents" | "gstRate" | "qstRate" | "depositPercent">): Totals {
  const subtotalCents = d.lines.reduce((n, l) => n + Math.round(l.quantity * l.unitPriceCents), 0);
  const taxableCents = subtotalCents - d.discountCents;
  const gstCents = Math.round(taxableCents * d.gstRate / 100);
  const qstCents = Math.round(taxableCents * d.qstRate / 100);
  const totalCents = taxableCents + gstCents + qstCents;
  return { subtotalCents, discountCents: d.discountCents, taxableCents, gstCents, qstCents, totalCents, depositCents: Math.round(totalCents * d.depositPercent / 100) };
}
export function cleanQuote(d: Record<string, unknown>): QuoteInput {
  if (!Array.isArray(d.lines) || d.lines.length < 1 || d.lines.length > 25) throw new Error("Ajoutez entre 1 et 25 lignes à la soumission.");
  const out: QuoteInput = {
    title: text(d.title, "Titre", 150, true), validUntil: dateOnly(d.validUntil, "Validité"), eventDate: dateOnly(d.eventDate, "Date de l’activité"),
    participants: d.participants === null ? null : numeric(d.participants, "Participants", 10000, true),
    location: text(d.location, "Lieu", 300), duration: text(d.duration, "Durée", 100),
    scope: text(d.scope, "Prestation", 3000), requirements: text(d.requirements, "Matériel", 2000), conditions: text(d.conditions, "Conditions", 3000),
    lines: d.lines.map((l: Record<string, unknown>) => ({ description: text(l?.description, "Description", 300, true), quantity: numeric(l?.quantity, "Quantité", 10000), unitPriceCents: numeric(l?.unitPriceCents, "Prix", 100000000, true) })),
    discountCents: numeric(d.discountCents, "Rabais", 100000000, true),
    gstRate: numeric(d.gstRate, "TPS", 20), qstRate: numeric(d.qstRate, "TVQ", 20), depositPercent: numeric(d.depositPercent, "Acompte", 100),
  };
  if (out.lines.some(l => l.quantity <= 0)) throw new Error("Les quantités doivent être supérieures à zéro.");
  if (calculateTotals(out).taxableCents < 0) throw new Error("Le rabais dépasse le sous-total.");
  if (calculateTotals(out).totalCents > 100000000) throw new Error("Montant trop élevé.");
  return out;
}
export function assertQuoteTransition(from: QuoteStatus, to: QuoteStatus, validUntil: string, today: string) {
  const allowed: Record<QuoteStatus, QuoteStatus[]> = { draft: ["sent"], sent: ["accepted", "declined"], accepted: [], declined: [] };
  if (!allowed[from]?.includes(to)) throw new Error("Ce changement d’état n’est pas permis. Dupliquez la soumission pour la réviser.");
  if ((to === "sent" || to === "accepted") && validUntil && validUntil < today) throw new Error("Cette soumission est expirée. Dupliquez-la et actualisez sa validité.");
}
export function isFollowUpDue(c: Company, today: string) { return Boolean(c.nextFollowUp && c.nextFollowUp <= today && !["completed", "lost"].includes(c.stage)) }
